import React, { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import TransactionList from '../components/Home/TransactionList';
import CardSummary from '../components/Home/CardSummary';
import Sidebar from '../components/Home/Sidebar';
import MainHeader from '../components/Home/MainHeader';
import TopExpenses from '../components/Home/TopExpenses';
import AccountSection from '../components/Home/AccountSection';
import AllCards from '../components/Home/AllCards';
import MonthlyStatistics from '../components/Home/MonthlyStatistics';
import BottomNavbar from '../components/Home/BottomNavbar';
import { useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import feather from 'feather-icons';
import { DeleteToken, GetData } from '../auth/Auth';
import { NEW_USER_KEY } from '../config/app.config';
import serverClient from '../api/ServerClient';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import { useLanguage } from '../context/LanguageContext';
import TransactionOptions from '../components/Home/TransactionOptions';
import { storeAccountsInCache } from '../util/cacheManager';
import { getPageMeta } from '../util/seoHelper';
import VoiceCommandButton from '../components/Home/VoiceCommandButton';
import useRefreshSignal from '../hooks/useRefreshSignal';

function Home({ handleDarkModeToggle, isDarkMode }) {
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const { t, language } = useLanguage();
    const [accounts, setAccounts] = useState(null);
    const [expenses, setExpenses] = useState(null);
    const [summary, setSummary] = useState(null);
    const [recentTransactions, setRecentTransactions] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [cards, setCards] = useState(null);
    const [user, setUser] = useState(null);
    const [performance, setPerformance] = useState(null);

    const fetchHomeData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get('/api/user/home');
            const homeAccounts = response?.data?.accounts?.filter(
                (item) => item.show_in_home === true
            );
            storeAccountsInCache(response?.data?.accounts);
            setAccounts(homeAccounts);
            setSummary(response?.data?.summary);
            setRecentTransactions(response?.data?.transactions);
            setExpenses(response?.data?.expenses);
            setCards(response?.data?.cards);
            setUser(response?.data?.user);
            setPerformance(response?.data?.performance);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [navigate, t]);

    useEffect(() => {
        feather.replace();
        setError('');
        const isNewUser = GetData(NEW_USER_KEY);
        if (isNewUser === 'true') {
            navigate('/new-user');
            return;
        }

        fetchHomeData();
    }, [fetchHomeData, navigate]);

    useRefreshSignal(refreshSignal, () => {
        setError('');
        fetchHomeData();
    });

    return (
        <>
            <Helmet>
                <title>{getPageMeta('home', language).title}</title>
                <meta name="description" content={getPageMeta('home', language).description} />
                <meta name="keywords" content={getPageMeta('home', language).keywords} />
                <link rel="canonical" href={getPageMeta('home', language).url} />

                {/* Open Graph Tags */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={getPageMeta('home', language).title} />
                <meta
                    property="og:description"
                    content={getPageMeta('home', language).description}
                />
                <meta property="og:url" content={getPageMeta('home', language).url} />
                <meta property="og:image" content={getPageMeta('home', language).image} />

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={getPageMeta('home', language).title} />
                <meta
                    name="twitter:description"
                    content={getPageMeta('home', language).description}
                />
                <meta name="twitter:image" content={getPageMeta('home', language).image} />

                {/* Structured Data */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name: language === 'bn' ? 'Xpenso ড্যাশবোর্ড' : 'Xpenso Dashboard',
                        description: getPageMeta('home', language).description,
                        url: getPageMeta('home', language).url,
                        image: getPageMeta('home', language).image,
                    })}
                </script>
            </Helmet>
            <MainHeader />
            {error && (
                <div className="p-4">
                    <Alert alert={'danger'}>{error}</Alert>
                </div>
            )}
            <Sidebar
                summary={summary}
                handleDarkModeToggle={handleDarkModeToggle}
                isDarkMode={isDarkMode}
            />
            <Loader loading={loading} />
            {accounts && <AccountSection accountData={accounts} refreshAccounts={fetchHomeData} />}
            {summary && <CardSummary user={user} summary={summary} refreshHome={fetchHomeData} />}
            <TransactionOptions refreshHome={fetchHomeData} />
            {recentTransactions && (
                <TransactionList
                    recentTransactions={recentTransactions}
                    refreshHome={fetchHomeData}
                />
            )}
            {expenses && <TopExpenses expenseData={expenses} />}
            {cards && cards.length > 0 && <AllCards cards={cards} />}

            {performance != null && <MonthlyStatistics performanceData={performance} />}
            <VoiceCommandButton refreshHome={fetchHomeData} />
            <BottomNavbar />
        </>
    );
}

export default Home;
